import ArrowNarrowRight from "@/assets/icons/arrow-narrow-right.svg";
import { useVistassistantMail } from "@/contexts/VistassistantMailContext";
import Mail from "@/domain/entities/emails/Email";
import useGetUserProfile from "@/hooks/useGetUserProfile";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import Spinner from "../ui/spinner";
import RecipientsTooltip from "./RecipientsTooltip";
import SenderAvatar from "./SenderAvatar";

const SENDER_NAME_MAX_WIDTH_LIST = "max-w-40";
const SENDER_NAME_MAX_WIDTH_SHORTENED = "max-w-18";
const SENDER_NAME_MAX_WIDTH_VISTASSISTANT = "max-w-[130px]";

type GroupAvatarsProps = {
    mail?: Mail;
    variant?: "default" | "noSender" | "shortened";
    showTooltip?: boolean;
};

const GroupAvatars = ({
    mail,
    variant = "default",
    showTooltip = true,
}: GroupAvatarsProps) => {
    const { data: userProfile, isLoading: isUserProfileLoading } =
        useGetUserProfile();
    const { t } = useTranslation();
    const { isVistassistantResult } = useVistassistantMail();

    const getSenderNameMaxWidth = () => {
        if (variant === "shortened") return SENDER_NAME_MAX_WIDTH_SHORTENED;
        if (isVistassistantResult) return SENDER_NAME_MAX_WIDTH_VISTASSISTANT;

        return SENDER_NAME_MAX_WIDTH_LIST;
    };

    if (!userProfile || isUserProfileLoading || !mail) {
        return <Spinner size="small" />;
    }

    const isSender = mail.from.email === userProfile?.profile.email;

    const totalRecipients = mail.to.length + mail.cc.length + mail.bcc.length;
    let remainingSlots = 3;

    const toShow = Math.min(remainingSlots, mail.to.length);
    remainingSlots -= toShow;

    const ccShow = Math.min(remainingSlots, mail.cc.length);
    remainingSlots -= ccShow;

    const bccShow = Math.min(remainingSlots, mail.bcc.length);
    const showOverflow = totalRecipients > 3;
    const overflowBadge = showOverflow ? (
        <RecipientsTooltip
            bcc={mail.bcc}
            cc={mail.cc}
            className="mr-[-4px]"
            currentUserProfile={userProfile}
            remainingCount={totalRecipients - 3}
            to={mail.to}
        />
    ) : null;

    return (
        <div className="flex items-center gap-2">
            {variant !== "noSender" && (
                <>
                    <div className="flex items-center gap-1 text-left">
                        <SenderAvatar
                            currentUserProfile={userProfile}
                            showTooltip={showTooltip}
                            user={mail.from}
                        />
                        <span
                            className={cn(
                                getSenderNameMaxWidth(),
                                "overflow-hidden text-ellipsis whitespace-nowrap text-xs",
                            )}
                        >
                            {isSender
                                ? t("email.youAreSender")
                                : mail.from.name}
                        </span>
                    </div>
                    <img alt="" src={ArrowNarrowRight} />
                </>
            )}
            <div className="flex items-center gap-2">
                {toShow > 0 && (
                    <div className="flex">
                        {variant === "noSender" && (
                            <SenderAvatar
                                className="mr-[-4px]"
                                currentUserProfile={userProfile}
                                showTooltip={showTooltip}
                                user={mail.from}
                            />
                        )}
                        {mail.to.slice(0, toShow).map((recipient) => (
                            <SenderAvatar
                                key={recipient.email}
                                className="mr-[-4px]"
                                currentUserProfile={userProfile}
                                showTooltip={showTooltip}
                                user={recipient}
                            />
                        ))}
                        {showOverflow &&
                            ccShow === 0 &&
                            bccShow === 0 &&
                            overflowBadge}
                    </div>
                )}
                {ccShow > 0 && (
                    <div className="flex items-center gap-0.5">
                        <span className="text-xs font-normal leading-none text-foreground/60">
                            {t("email.cc").toUpperCase()}:
                        </span>
                        <div className="flex items-center">
                            {mail.cc.slice(0, ccShow).map((recipient) => (
                                <SenderAvatar
                                    key={recipient.email}
                                    className="mr-[-4px]"
                                    currentUserProfile={userProfile}
                                    showTooltip={showTooltip}
                                    user={recipient}
                                />
                            ))}
                            {showOverflow && bccShow === 0 && overflowBadge}
                        </div>
                    </div>
                )}
                {bccShow > 0 && (
                    <div className="flex items-center gap-0.5">
                        <span className="text-xs font-normal leading-none text-foreground/60">
                            {t("email.bcc").toUpperCase()}:
                        </span>
                        <div className="flex items-center">
                            {mail.bcc.slice(0, bccShow).map((recipient) => (
                                <SenderAvatar
                                    key={recipient.email}
                                    className="mr-[-4px]"
                                    currentUserProfile={userProfile}
                                    showTooltip={showTooltip}
                                    user={recipient}
                                />
                            ))}
                            {showOverflow && overflowBadge}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GroupAvatars;
